import React, { FC, useEffect, useMemo, useState } from 'react';
import { T } from 'src/shared/helpers';
import styled, { keyframes } from 'styled-components';

const textFade = keyframes`
	0%, 100% {
		opacity: 0;
		transform: translateY(4px);
	}
	10%, 90% {
		opacity: 1;
		transform: translateY(0);
	}
`;

function computeProgress(startedAt: number): number {
    const elapsed = (Date.now() - startedAt) / 1000;
    const duration = 120;
    const t = Math.min(elapsed / duration, 1);
    return 95 * (1 - Math.pow(1 - t, 3));
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 40px 20px;
    min-height: 200px;
`;

const RotatingText = styled.div`
    font-size: 13px;
    color: #78909c;
    animation: ${textFade} 5s ease-in-out infinite;
`;

const ProgressBarContainer = styled.div`
    width: 80%;
    height: 4px;
    background: rgba(147, 51, 234, 0.15);
    border-radius: 2px;
    overflow: hidden;
`;

const ProgressBar = styled.div<{ $width: number }>`
    height: 100%;
    width: ${(props) => props.$width}%;
    background: linear-gradient(90deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    border-radius: 2px;
    transition: width 1s ease-out;
`;

interface AIGeneratingIndicatorProps {
    phrases: string[];
}

export const AIGeneratingIndicator: FC<AIGeneratingIndicatorProps> = ({ phrases }) => {
    const [phraseIndex, setPhraseIndex] = useState(0);
    const [startedAt] = useState(() => Date.now());
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setPhraseIndex((prev) => (prev + 1) % phrases.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [phrases]);

    useEffect(() => {
        const interval = setInterval(() => {
            setProgress(computeProgress(startedAt));
        }, 1000);
        return () => clearInterval(interval);
    }, [startedAt]);

    return (
        <Container>
            <RotatingText key={phraseIndex}>{T._(phrases[phraseIndex], 'Admin')}</RotatingText>
            <ProgressBarContainer>
                <ProgressBar $width={progress} />
            </ProgressBarContainer>
        </Container>
    );
};
